import * as React from 'react';
import { TextAreaGreyStyleLayout } from '@doltech/ui/lib/figma/Input/TextAreaGreyStyleLayout/TextAreaGreyStyleLayout';
import styled, { keyframes } from 'styled-components';
import { IconSystemV2MediaTestMic } from '@doltech/icons/SystemV2/IconSystemV2MediaTestMic';
import { IconSystemV2MediaRecording } from '@doltech/icons/SystemV2/IconSystemV2MediaRecording';
import { IconSystemV2EssentialWarningFill } from '@doltech/icons/SystemV2/IconSystemV2EssentialWarningFill';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { DolPopover } from '@doltech/ui/lib/figma/Popover/DolPopover';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import cl from 'classnames';
import { HighlightWithinTextarea } from 'react-highlight-within-textarea';
import { scrollBarStyles } from '@doltech/utils/lib/css-style';
import { useFromScreens } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';

const Main = styled.div`
  width: 100%;
  position: relative;
  .custom-inner-content {
    background-color: red;
  }
  &.disabled {
    textarea {
      cursor: not-allowed;
    }
  }
`;

const Wrapper = styled.div`
  width: 100%;
  padding: 16px 20px;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;

  .in-correct {
    color: ${colorsV2.red100};
    background: transparent;
  }

  mark {
    color: ${colorsV2.grey180};
    background: transparent;
  }

  .correct {
    color: ${colorsV2.green100} !important;
    background: transparent;
  }

  &.span-black {
    span {
      color: ${colorsV2.grey180};
    }
  }
  .free-text-area-container {
    width: 100%;
  }
  .free-text-area {
    caret-color: ${colorsV2.grey180};
    color: ${colorsV2.grey180};
    width: 100%;
    height: 80px;
    border: 1px solid transparent;
    outline: none;
    resize: none;

    ${scrollBarStyles({
      trackColor: colorsV2.grey10,
      thumbColor: colorsV2.gray40,
    })};

    &::placeholder {
      color: ${colorsV2.black60};
    }

    &:disabled {
      color: ${colorsV2.black80};
    }
    &.checked {
      color: transparent;
    }
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Icon = styled.div`
  position: absolute;
  right: 20px;
  bottom: 16px;
  cursor: pointer;
  .disabled {
    cursor: not-allowed;
  }
  .recording {
    animation: ${fadeIn} 1s infinite;
  }
`;

const PopoverContent = styled.div`
  .guide {
    opacity: 0.8;
    cursor: pointer;
  }
`;

interface InputFreeTextDictationStudentProps {
  listening?: boolean;
  isMicrophoneDisabled?: boolean;
  value?: string;
  disabled?: boolean;
  isIconHidden?: boolean;
  highlight?: any;
  checked?: boolean;
  startListening?: () => void;
  stopListening?: () => void;
  onChange?: (value) => void;
  onCheck?: () => void;
}

export const InputFreeTextDictationStudent = (props: InputFreeTextDictationStudentProps) => {
  const {
    listening,
    isMicrophoneDisabled,
    value,
    disabled = false,
    isIconHidden = false,
    highlight,
    checked = false,
    startListening,
    stopListening,
    onChange,
    onCheck,
  } = props;
  const [, from1025] = useFromScreens([0, 1025]);
  const renderIcon = React.useCallback(() => {
    if (isMicrophoneDisabled) {
      return <IconSystemV2EssentialWarningFill size={24} color={colorsV2.yellow100} />;
    }
    if (listening) {
      return (
        <IconSystemV2MediaRecording
          size={24}
          color={colorsV2.primary100}
          onClick={stopListening}
          className={cl({ disabled }, 'recording')}
        />
      );
    }
    return (
      <IconSystemV2MediaTestMic
        size={24}
        color={colorsV2.neutralSecondary}
        onClick={disabled ? null : startListening}
        className={cl({ disabled })}
      />
    );
  }, [listening, isMicrophoneDisabled, disabled, startListening, stopListening]);

  const placeholderText = () => {
    if (isMicrophoneDisabled) {
      return 'Không thể thu âm';
    }
    if (listening) {
      return 'Bắt đầu nói';
    }
    return 'Nhập những gì bạn nghe được';
  };

  const tooltipContent = React.useCallback(() => {
    if (isMicrophoneDisabled) {
      return (
        <ResponsiveTypography.Paragraph variant="Regular/14-20" color="grey180">
          Bạn cần cấp quyền truy cập microphone để thu âm.
        </ResponsiveTypography.Paragraph>
      );
    }
    if (listening) {
      return (
        <ResponsiveTypography.Paragraph variant="Regular/14-20" color="grey180">
          Dừng thu âm
        </ResponsiveTypography.Paragraph>
      );
    }
    return (
      <ResponsiveTypography.Paragraph variant="Regular/14-20" color="grey180">
        Speech to text
      </ResponsiveTypography.Paragraph>
    );
  }, [isMicrophoneDisabled, listening]);

  return (
    <Main className={cl({ disabled })}>
      <TextAreaGreyStyleLayout
        textAreaContent={
          <Wrapper className={cl({ 'span-black': checked })}>
            <HighlightWithinTextarea
              placeholder={placeholderText()}
              containerClassName="free-text-area-container"
              className={cl({ checked: checked }, 'free-text-area')}
              value={value}
              onChange={(event) => {
                onChange?.(event.target.value);
              }}
              highlight={highlight}
              spellCheck="false"
              onKeyDown={(event) => {
                if (event?.key === 'Enter') {
                  event?.preventDefault();
                  onCheck?.();
                  return '';
                }
              }}
              disabled={disabled}
            />
          </Wrapper>
        }
      />
      {isIconHidden ? null : (
        <Icon>
          <DolPopover
            placement="top-end"
            trigger={['hover']}
            overlayClassName="no-arrow custom-inner-content"
            content={<PopoverContent>{tooltipContent()}</PopoverContent>}
            disabled={!from1025}
          >
            {renderIcon()}
          </DolPopover>
        </Icon>
      )}
    </Main>
  );
};
